/**
 * Admin — Email Templates.
 *
 * The copy used when the estimator itself emails a share (the direct-SMTP sender —
 * testing and super-admin fallback only; production shares go through the Pardot
 * form handler, whose email lives in Pardot). One template per key + locale; the
 * sender walks the locale fallback chain to English.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Loader2, Save, RotateCcw, Eye, Send, AlertTriangle } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  fetchEmailTemplates, saveEmailTemplate, previewEmailTemplate, resetEmailTemplate, testEmailTemplate,
  type EmailTemplate, type EmailTemplatesResponse,
} from '../../services/adminEmailTemplatesApi';

export const AdminEmailTemplates: React.FC = () => {
  const { isSuperAdmin } = useAdminAuth();
  const [data, setData] = useState<EmailTemplatesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [key, setKey] = useState('share_recipient');
  const [locale, setLocale] = useState('en');
  const [draft, setDraft] = useState<{ subject: string; htmlBody: string; textBody: string; isActive: boolean }>({ subject: '', htmlBody: '', textBody: '', isActive: true });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ subject: string; html: string; text: string } | null>(null);
  const [testTo, setTestTo] = useState('');

  const load = async () => {
    try { setData(await fetchEmailTemplates()); } catch (e) { setError(e instanceof Error ? e.message : 'Could not load templates'); }
  };
  useEffect(() => { void load(); }, []);

  const current: EmailTemplate | undefined = useMemo(() => data?.templates.find(t => t.key === key && t.locale === locale), [data, key, locale]);
  useEffect(() => {
    setDraft(current ? { subject: current.subject, htmlBody: current.htmlBody, textBody: current.textBody, isActive: current.isActive } : { subject: '', htmlBody: '', textBody: '', isActive: true });
    setPreview(null);
  }, [current?.key, current?.locale, current?.updatedAt]);

  const keyMeta = data?.keys.find(k => k.key === key);
  const hasDefault = keyMeta?.hasDefaults.includes(locale) ?? false;
  const dirty = !current || draft.subject !== current.subject || draft.htmlBody !== current.htmlBody || draft.textBody !== current.textBody || draft.isActive !== current.isActive;

  const run = async (fn: () => Promise<string>) => {
    setBusy(true); setError(null); setMessage(null);
    try { setMessage(await fn()); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Request failed'); } finally { setBusy(false); }
  };

  const doPreview = async () => {
    setBusy(true); setError(null);
    try { setPreview(await previewEmailTemplate(key, locale, { subject: draft.subject, htmlBody: draft.htmlBody, textBody: draft.textBody })); }
    catch (e) { setError(e instanceof Error ? e.message : 'Preview failed'); }
    finally { setBusy(false); }
  };

  if (!data && !error) return <div className="text-muted small d-flex align-items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

  return (
    <div>
      <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2"><Mail size={18} /> Email Templates</h5>
        <p className="text-muted small mb-0">
          Used only when the estimator sends a share itself — the <strong>Estimator SMTP</strong> sender (testing / fallback) and the redirect controls under Settings → Integrations → Estimate Sharing.
          Production shares go through the Pardot form handler, whose email is built in Pardot. Placeholders: {keyMeta?.placeholders.map(p => <code key={p} className="me-1">{`{{${p}}}`}</code>)}
          and conditional blocks <code>{'{{#message}}…{{/message}}'}</code>. <code>{'{{shareUrl}}'}</code> is required.
          {data && !data.smtpConfigured && <span className="text-warning d-block mt-1"><AlertTriangle size={13} /> SMTP is not configured, so test sends will fail.</span>}
        </p>
      </div>

      {message && <div className="alert alert-success py-2 small">{message}</div>}
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <div className="row g-3">
        <div className="col-12 col-lg-3">
          <div className="bg-white rounded-3 shadow-sm p-3">
            <label className="form-label small fw-semibold mb-1">Template</label>
            <select className="form-select form-select-sm mb-3" value={key} onChange={e => setKey(e.target.value)}>
              {data?.keys.map(k => <option key={k.key} value={k.key}>{k.key}</option>)}
            </select>
            <label className="form-label small fw-semibold mb-1">Locale</label>
            <div className="list-group list-group-flush small">
              {data?.locales.map(l => {
                const exists = data.templates.some(t => t.key === key && t.locale === l.code);
                return (
                  <button type="button" key={l.code} className={`list-group-item list-group-item-action px-2 py-1 d-flex justify-content-between ${locale === l.code ? 'active' : ''}`} onClick={() => setLocale(l.code)}>
                    <span>{l.name} <span className="opacity-75">({l.code})</span></span>
                    <span>{exists ? '●' : <span className="opacity-50">fallback</span>}</span>
                  </button>
                );
              })}
            </div>
            <div className="form-text mt-2">A locale without its own row falls back along the locale chain to English at send time.</div>
          </div>
        </div>

        <div className="col-12 col-lg-9">
          <div className="bg-white rounded-3 shadow-sm p-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h6 className="fw-semibold mb-0">{key} · {locale}{!current && <span className="badge text-bg-secondary ms-2">no row yet</span>}</h6>
              <div className="d-flex flex-wrap gap-2">
                <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" disabled={busy} onClick={() => void doPreview()}><Eye size={13} /> Preview</button>
                {isSuperAdmin && hasDefault && <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" disabled={busy} onClick={() => { if (window.confirm('Replace this template with the built-in copy?')) void run(async () => { await resetEmailTemplate(key, locale); return 'Built-in copy restored.'; }); }}><RotateCcw size={13} /> Reset to built-in</button>}
                {isSuperAdmin && <button type="button" className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1" disabled={busy || !dirty} onClick={() => void run(async () => { await saveEmailTemplate(key, locale, draft); return 'Template saved.'; })}>{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save</button>}
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label small fw-semibold mb-1">Subject</label>
              <input className="form-control form-control-sm" value={draft.subject} disabled={!isSuperAdmin} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))} />
            </div>
            <div className="mb-2">
              <label className="form-label small fw-semibold mb-1">HTML body</label>
              <textarea className="form-control form-control-sm font-monospace" rows={14} value={draft.htmlBody} disabled={!isSuperAdmin} onChange={e => setDraft(d => ({ ...d, htmlBody: e.target.value }))} spellCheck={false} />
              <div className="form-text">Inline styles only (email clients ignore stylesheets). Scripts and event handlers are stripped on save.</div>
            </div>
            <div className="mb-2">
              <label className="form-label small fw-semibold mb-1">Plain-text body</label>
              <textarea className="form-control form-control-sm font-monospace" rows={6} value={draft.textBody} disabled={!isSuperAdmin} onChange={e => setDraft(d => ({ ...d, textBody: e.target.value }))} spellCheck={false} />
            </div>
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <div className="form-check form-switch mb-0">
                <input id="et-active" type="checkbox" className="form-check-input" role="switch" checked={draft.isActive} disabled={!isSuperAdmin} onChange={e => setDraft(d => ({ ...d, isActive: e.target.checked }))} />
                <label htmlFor="et-active" className="form-check-label small">Active (inactive rows are skipped in the fallback chain)</label>
              </div>
              {isSuperAdmin && (
                <div className="input-group input-group-sm ms-auto" style={{ maxWidth: 380 }}>
                  <input type="email" className="form-control" placeholder="Send a test (sample data) to…" value={testTo} onChange={e => setTestTo(e.target.value)} />
                  <button type="button" className="btn btn-outline-secondary d-inline-flex align-items-center gap-1" disabled={busy || !testTo.trim()} onClick={() => void run(async () => { const r = await testEmailTemplate(key, locale, testTo.trim(), draft); if (!r.ok) throw new Error(r.error || 'Test send failed'); return `Test sent to ${testTo.trim()}.`; })}><Send size={13} /> Send test</button>
                </div>
              )}
            </div>
            {preview && (
              <div className="border rounded-3 p-2">
                <div className="small mb-2"><strong>Subject:</strong> {preview.subject}</div>
                <iframe title="Email preview" sandbox="" srcDoc={preview.html} style={{ width: '100%', height: 480, border: '1px solid #dee2e6', borderRadius: 6, background: '#fff' }} />
                <details className="small mt-2"><summary className="text-muted">Plain-text version</summary><pre className="mb-0 mt-1" style={{ whiteSpace: 'pre-wrap' }}>{preview.text}</pre></details>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplates;
